// ─────────────────────────────────────────────────────────────────────
// ELO DATA — pure derivations behind the mobile «ELO» screen: distance to
// the next level, and a reconstructed ELO timeline. Players carry no stored
// ELO history, so the timeline is rebuilt backwards from the current rating
// using every recorded change: single matches (result.t1Change/t2Change),
// Padel24/Americano matches (eloChange1/2) and KM/seriespill matches
// (teamEloChange.t1/t2). Walkovers and zero changes are skipped.
// ─────────────────────────────────────────────────────────────────────
import { ELO_LEVELS, getEloLevel, type EloLevel } from '../../engine';
import { matchSideOf, memberSingleMatches } from './memberData';

export interface NextLevelInfo {
  current: EloLevel;
  next: EloLevel | null;
  /** Points still needed to reach `next` (0 at the top level). */
  pointsToNext: number;
  /** 0–1 progress through the current level's band. */
  progress: number;
}

export function nextLevelInfo(elo: number): NextLevelInfo {
  const e = Math.round(elo || 0);
  const current = getEloLevel(e);
  const idx = ELO_LEVELS.findIndex((l) => l.id === current.id);
  const next = idx >= 0 && idx < ELO_LEVELS.length - 1 ? ELO_LEVELS[idx + 1] : null;
  const pointsToNext = next ? Math.max(0, next.min - e) : 0;
  const span = next ? next.min - current.min : 1;
  const progress = next ? Math.min(1, Math.max(0, (e - current.min) / span)) : 1;
  return { current, next, pointsToNext, progress };
}

export interface EloEvent {
  ts: number;
  delta: number;
  /** «Enkeltkamp», tournament name, … */
  label: string;
  kind: 'single' | 'tournament';
  /** Opponents as ids (for name lookup in the UI). */
  opponents: any[];
  /** ELO after this event (filled by eloTimeline). */
  elo: number;
}

const teamIds = (team: any): any[] =>
  [team?.p1?.playerId ?? team?.p1Id, team?.p2?.playerId ?? team?.p2Id].filter((x) => x != null);

/**
 * Every ELO change the member has been part of, oldest first, each carrying
 * the rating AFTER it (walked backwards from `currentElo`).
 */
export function eloTimeline({
  member,
  singleMatches = [],
  history = [],
}: {
  member: any;
  singleMatches?: any[];
  history?: any[];
}): EloEvent[] {
  if (!member) return [];
  const events: Omit<EloEvent, 'elo'>[] = [];

  memberSingleMatches(singleMatches, member.id).forEach((pm) => {
    if (pm.eloChange == null || pm.eloChange === 0) return;
    const them = pm.myTeam === 1 ? pm.match.team2 : pm.match.team1;
    events.push({
      ts: pm.playedAt || 0,
      delta: pm.eloChange,
      label: 'Enkeltkamp',
      kind: 'single',
      opponents: teamIds(them),
    });
  });

  (history || []).forEach((h: any) => {
    const t = h.tournament || {};
    const name = t.config?.name || t.name || 'Turnering';
    const fallbackTs = t.archivedAt || t.completedAt || t.createdAt || 0;
    (h.matches || []).forEach((m: any) => {
      if (!m || m.score?.walkover) return;
      const side = matchSideOf(m, member.id);
      if (!side) return;
      let delta: number | null = null;
      if (typeof m.eloChange1 === 'number' || typeof m.eloChange2 === 'number') {
        delta = side === 1 ? m.eloChange1 : m.eloChange2;
      } else if (m.teamEloChange) {
        delta = side === 1 ? m.teamEloChange.t1 : m.teamEloChange.t2;
      }
      if (typeof delta !== 'number' || delta === 0) return;
      const them = side === 1 ? m.team2 : m.team1;
      events.push({
        ts: m.playedAt || m.completedAt || fallbackTs,
        delta,
        label: name,
        kind: 'tournament',
        opponents: teamIds(them),
      });
    });
  });

  events.sort((a, b) => a.ts - b.ts);
  // Walk backwards from now: the rating after the last event is the current one.
  let elo = Math.round(member.elo || 0);
  const out: EloEvent[] = new Array(events.length);
  for (let i = events.length - 1; i >= 0; i--) {
    out[i] = { ...events[i], elo };
    elo -= events[i].delta;
  }
  return out;
}

/** Sum of the deltas newer than `sinceMs` (e.g. the last 30 days). */
export function eloTrend(events: EloEvent[], sinceMs: number): number {
  return events.filter((e) => e.ts >= sinceMs).reduce((s, e) => s + e.delta, 0);
}
